import React, { useEffect, useMemo, useState } from "react";
import { doc, writeBatch, deleteField, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { showAlert } from "../../alertService";
import { footprintDesdeResumen } from "../../utils/espacios";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import EspaciosPicker from "../shared/EspaciosPicker";

const ACCENT = "#1b4d2e";

// Asistente para migrar los eventos que quedaron sin espacio cuando el venue
// encendió multiespacio. Cada uno bloquea el venue entero hasta que alguien
// decida, y hacerlo de a uno desde el calendario no es viable con cientos.
//
// Nunca adivina: asignar un espacio equivocado LIBERA una fecha y puede
// provocar un empalme. Por eso solo hay dos salidas por evento, ambas
// explícitas — elegir espacios, o confirmar que sí toma todo el venue
// (`ocupaTodoElVenue`, marca de UI que apaga el aviso sin tocar el cálculo de
// conflictos).

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

const fechaCorta = (ev) => {
  const d = toDate(ev.startTime);
  if (!d) return "sin fecha";
  return `${d.getDate()} ${MESES[d.getMonth()]} ${d.getFullYear()}`;
};

// Firestore tope 500 escrituras por batch; un venue con cientos de eventos
// legacy se pasa de largo si se manda todo junto.
const TOPE_BATCH = 450;

export default function AsignarEspaciosWizard({
  open,
  onClose,
  venueId,
  eventos = [],
  espaciosActivos = [],
  espaciosPorId = {},
  userInfo,
  user,
}) {
  // Selección por evento; `TODO` es la confirmación explícita de venue entero.
  const [seleccion, setSeleccion] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [bulk, setBulk] = useState([]);

  useEffect(() => {
    if (open) setSeleccion({});
  }, [open]);

  // Decidido = espacios elegidos (al menos uno) o "TODO" confirmado. Una
  // selección vacía NO decide nada: escribiría `unidadesOcupadas: []`, que
  // sigue bloqueando todo el venue Y sigue disparando el aviso de "sin
  // espacio asignado" — un "Listo" mentiroso.
  const decididos = useMemo(
    () =>
      eventos.filter((ev) => {
        const v = seleccion[ev.id];
        return v === "TODO" || (Array.isArray(v) && v.length > 0);
      }),
    [eventos, seleccion]
  );

  if (!open) return null;

  // Deseleccionar el último chip (o quitar "TODO") regresa el evento a
  // "sin decidir" en vez de dejar un [] colgando en la selección.
  const setEvento = (id, valor) =>
    setSeleccion((prev) => {
      if (valor === undefined || (Array.isArray(valor) && valor.length === 0)) {
        const { [id]: _omitido, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: valor };
    });

  const aplicarATodos = (valor) => {
    const next = {};
    eventos.forEach((ev) => {
      next[ev.id] = valor;
    });
    setSeleccion(next);
  };

  const guardar = async () => {
    if (decididos.length === 0) return;
    setGuardando(true);
    try {
      for (let i = 0; i < decididos.length; i += TOPE_BATCH) {
        const lote = decididos.slice(i, i + TOPE_BATCH);
        const batch = writeBatch(db);
        lote.forEach((ev) => {
          const ref = doc(db, "venues", venueId, "calendars", "default", "events", ev.id);
          const val = seleccion[ev.id];
          if (val === "TODO") {
            // El footprint sigue ausente (= todo el venue). Solo se registra
            // que un humano lo revisó, para que el aviso deje de salir.
            batch.update(ref, {
              ocupaTodoElVenue: true,
              updatedAt: serverTimestamp(),
            });
          } else {
            const fp = footprintDesdeResumen(val, espaciosPorId);
            batch.update(ref, {
              espaciosIds: val,
              unidadesOcupadas: fp ? [...fp].sort() : [],
              ocupaTodoElVenue: deleteField(),
              updatedAt: serverTimestamp(),
            });
          }
        });
        await batch.commit();
      }
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.EVENT_EDIT,
          entity: "event",
          entityId: "bulk",
          entityName: `${decididos.length} eventos`,
          details: { asignacionMasivaDeEspacios: decididos.length },
        },
        userInfo,
        user
      );
      showAlert(
        `${decididos.length} evento${decididos.length === 1 ? "" : "s"} actualizado${decididos.length === 1 ? "" : "s"}`,
        "success"
      );
      onClose();
    } catch (err) {
      console.error("Error asignando espacios:", err);
      showAlert("Error al guardar. Ningún cambio parcial se revierte solo — revisa la lista.", "error");
    }
    setGuardando(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={guardando ? undefined : onClose} />
      <div className="relative flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900">
        <div className="border-b border-zinc-200 px-5 py-4 dark:border-zinc-700/60">
          <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
            Asignar espacios a eventos existentes
          </h3>
          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            Estos {eventos.length} eventos próximos se crearon antes de que
            activaras la reserva por salón, así que hoy bloquean todos tus
            espacios — en tu calendario y en tu página pública. Elige dónde
            ocurre cada uno, o confirma que toma el venue completo.
          </p>
        </div>

        {/* Acciones masivas: el caso común es "todos en el mismo salón" o
            "todos son de venue completo". */}
        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 bg-zinc-50 px-5 py-3 dark:border-zinc-700/40 dark:bg-zinc-800/40">
          <span className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
            Aplicar a todos
          </span>
          <EspaciosPicker
            opciones={espaciosActivos}
            value={bulk}
            onChange={setBulk}
            size="sm"
          />
          <button
            type="button"
            disabled={bulk.length === 0}
            onClick={() => aplicarATodos(bulk)}
            className="rounded-lg px-2.5 py-1 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-40"
            style={{ backgroundColor: ACCENT }}
          >
            Aplicar
          </button>
          <button
            type="button"
            onClick={() => aplicarATodos("TODO")}
            className="rounded-lg bg-zinc-200 px-2.5 py-1 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-200"
          >
            Todos ocupan el venue completo
          </button>
          {decididos.length > 0 && (
            <button
              type="button"
              onClick={() => setSeleccion({})}
              className="ml-auto text-xs font-semibold text-zinc-500 hover:underline"
            >
              Limpiar
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
          <div className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
            {eventos.map((ev) => {
              const val = seleccion[ev.id];
              const esTodo = val === "TODO";
              return (
                <div key={ev.id} className="py-3">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {ev.nombre || "Sin nombre"}
                    </span>
                    <span className="text-xs text-zinc-400">{fechaCorta(ev)}</span>
                    {val !== undefined && (
                      <span
                        className="ml-auto shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{ backgroundColor: ACCENT + "1a", color: ACCENT }}
                      >
                        Listo
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <EspaciosPicker
                      opciones={espaciosActivos}
                      value={esTodo || val === undefined ? [] : val}
                      onChange={(ids) => setEvento(ev.id, ids)}
                      size="sm"
                    />
                    <button
                      type="button"
                      onClick={() => setEvento(ev.id, esTodo ? undefined : "TODO")}
                      className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition ${
                        esTodo
                          ? "border-transparent text-white"
                          : "border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300"
                      }`}
                      style={esTodo ? { backgroundColor: ACCENT } : undefined}
                    >
                      Venue completo
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-zinc-200 px-5 py-4 dark:border-zinc-700/60">
          <span className="text-xs text-zinc-500">
            {decididos.length} de {eventos.length} decididos
            {decididos.length < eventos.length && " — los demás quedan igual"}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={guardando}
              className="rounded-lg bg-zinc-100 px-4 py-2 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-200 disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-300"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={guardar}
              disabled={guardando || decididos.length === 0}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-40"
              style={{ backgroundColor: ACCENT }}
            >
              {guardando ? "Guardando..." : `Guardar ${decididos.length}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
